import { cacheService } from './cacheService';
import { offlineDatabase, OfflineResponse, OfflineReview } from './offlineDatabase';
import { ReviewItem, ReviewTemplate } from '../types';
import { cloneTemplate } from '../utils/cloneTemplate';
import {
  filterCategoriesForReview,
  mergeDeprecatedItems,
  pruneEmptyCategories,
} from '../utils/historicalTemplateFilters';

export interface OfflineReviewHydrationDependencies {
  getReviewTemplateSnapshot?: (reviewId: number) => Promise<ReviewTemplate | null>;
  getCachedTemplates: () => Promise<ReviewTemplate[]>;
  getTemplates: () => Promise<ReviewTemplate[]>;
}

function defaultDependencies(): OfflineReviewHydrationDependencies {
  const snapshotFn = (offlineDatabase as any)?.getReviewTemplateSnapshot;

  return {
    getReviewTemplateSnapshot: typeof snapshotFn === 'function'
      ? snapshotFn.bind(offlineDatabase)
      : undefined,
    getCachedTemplates: () => offlineDatabase.getCachedTemplates(),
    getTemplates: () => cacheService.getTemplates(),
  };
}

export function serializeReviewItem(item: any): ReviewItem | undefined {
  if (!item) {
    return undefined;
  }

  try {
    const raw = item.toJSON ? item.toJSON() : item;
    return JSON.parse(JSON.stringify(raw));
  } catch (error) {
    console.warn('[OfflineReviewService] Failed to serialize review item metadata:', error);
    return undefined;
  }
}

function buildResponsesForMerge(responses: OfflineResponse[]): Array<{ item: ReviewItem }> {
  return responses
    .filter(response => !!response.itemSnapshot)
    .map(response => ({
      item: response.itemSnapshot!,
    }));
}

export async function loadTemplateFromCaches(
  templateId: number,
  dependencies: OfflineReviewHydrationDependencies = defaultDependencies()
): Promise<ReviewTemplate | null> {
  const templates = await dependencies.getTemplates();
  let template = templates.find(t => t.id === templateId);

  if (!template || !template.categories || template.categories.length === 0) {
    try {
      console.log('[OfflineReviewService] Cache template missing categories, reloading from DB');
      const dbTemplates = await dependencies.getCachedTemplates();
      const dbTemplate = dbTemplates.find(t => t.id === templateId);
      if (dbTemplate && dbTemplate.categories && dbTemplate.categories.length > 0) {
        template = dbTemplate;
      }
    } catch (error) {
      console.warn('[OfflineReviewService] Fallback template lookup failed:', error);
    }
  }

  return template || null;
}

export async function buildTemplateForOfflineReview(
  offlineReview: OfflineReview,
  responses: OfflineResponse[],
  dependencies: OfflineReviewHydrationDependencies = defaultDependencies()
): Promise<ReviewTemplate | null> {
  let snapshot: ReviewTemplate | null = null;

  if (dependencies.getReviewTemplateSnapshot) {
    try {
      snapshot = await dependencies.getReviewTemplateSnapshot(offlineReview.id);
      if (snapshot) {
        console.log('[OfflineReviewService] Using stored snapshot for review', offlineReview.id);
      }
    } catch (error) {
      console.warn('[OfflineReviewService] Failed to load template snapshot:', error);
      snapshot = null;
    }
  }

  if (snapshot) {
    return cloneTemplate(snapshot);
  }

  const fallback = await loadTemplateFromCaches(offlineReview.templateId, dependencies);
  if (!fallback) {
    console.warn('[OfflineReviewService] No template available for review', offlineReview.id);
    return null;
  }

  const template = cloneTemplate(fallback);
  const responsesForMerge = buildResponsesForMerge(responses);

  if (responsesForMerge.length > 0) {
    template.categories = mergeDeprecatedItems(template.categories || [], responsesForMerge);
  }

  const reviewCreatedAt = offlineReview.createdAt ? new Date(offlineReview.createdAt) : new Date();
  template.categories = filterCategoriesForReview(template.categories || [], reviewCreatedAt);
  template.categories = pruneEmptyCategories(template.categories || []);

  console.log('[OfflineReviewService] Built fallback template for review', offlineReview.id, {
    categories: template.categories?.length || 0,
  });

  return template;
}
